import { useAuth } from "@/context/AuthContext";
import { SaveDialogContent, SaveButton, SaveForm } from "@/components/SaveForm";
import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Boxes, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { nome: "", code: "", descricao: "", valor_adicional: 0, ativo: true };
const inp = "bg-slate-950 border-slate-700 text-slate-100 mt-1";

export default function PlatformModules() {
  const { user } = useAuth();
  const owner = user?.is_platform_owner === true;
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/platform/modules").then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const saveNew = async () => {
    if (!form.nome) return toast.error("Informe o nome do módulo.");
    setBusy(true);
    try {
      await api.post("/platform/modules", form);
      toast.success("Módulo criado.");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/platform/modules/${edit.id}`, owner ? edit : { nome: edit.nome, descricao: edit.descricao });
      toast.success("Módulo atualizado.");
      setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50">Módulos</h1>
          <p className="text-sm text-slate-400">Catálogo de recursos que poderão compor os planos e assinaturas.</p>
        </div>
        {owner && <Button onClick={() => setOpen(true)} className="bg-violet-700 hover:bg-violet-600"><Plus className="h-4 w-4 mr-1" /> Novo módulo</Button>}
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {list.map((m) => (
          <Card key={m.id} className="card-metal p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex gap-3">
                <div className="h-9 w-9 rounded-lg bg-violet-900/30 border border-violet-800/50 flex items-center justify-center"><Boxes className="h-4 w-4 text-violet-300" /></div>
                <div>
                  <div className="font-semibold text-slate-100">{m.nome}</div>
                  <div className="text-[11px] font-mono text-slate-500">{m.code}</div>
                </div>
              </div>
              <Button aria-label="Editar módulo" size="icon" variant="ghost" onClick={() => setEdit({ ...m })}><Pencil className="h-4 w-4" /></Button>
            </div>
            <p className="mt-3 text-sm text-slate-400 min-h-10">{m.descricao || "Sem descrição."}</p>
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className={m.ativo ? "text-emerald-400" : "text-rose-400"}>{m.ativo ? "Ativo" : "Inativo"}</span>
              <span className="text-slate-400">Adicional: R$ {Number(m.valor_adicional || 0).toFixed(2).replace(".", ",")}</span>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <SaveForm disabled={busy} onSave={saveNew} className="space-y-4">
            <DialogHeader><DialogTitle>Novo módulo</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome *</Label><Input className={inp} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
              <div><Label>Código</Label><Input className={inp} value={form.code} placeholder="Gerado pelo nome se vazio" onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
              <div><Label>Descrição</Label><Input className={inp} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
              <div><Label>Valor adicional mensal</Label><Input type="number" step="0.01" className={inp} value={form.valor_adicional} onChange={(e) => setForm({ ...form, valor_adicional: e.target.value })} /></div>
            </div>
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><SaveButton type="submit" disabled={busy} className="bg-violet-700 hover:bg-violet-600">Salvar</SaveButton></DialogFooter>
          </SaveForm>
        </SaveDialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <SaveForm disabled={busy} onSave={saveEdit} className="space-y-4">
            <DialogHeader><DialogTitle>Editar módulo</DialogTitle></DialogHeader>
            {!owner && <p className="text-sm text-slate-300">Edite nome e descrição. Preço e ativação global são exclusivos do proprietário.</p>}
            {edit && <div className="space-y-3">
              <div><Label>Nome</Label><Input className={inp} value={edit.nome || ""} onChange={(e) => setEdit({ ...edit, nome: e.target.value })} /></div>
              <div><Label>Descrição</Label><Input className={inp} value={edit.descricao || ""} onChange={(e) => setEdit({ ...edit, descricao: e.target.value })} /></div>
              <div><Label>Valor adicional mensal</Label><Input readOnly={!owner} aria-label="Valor adicional mensal" type="number" step="0.01" className={inp} value={edit.valor_adicional ?? 0} onChange={(e) => setEdit({ ...edit, valor_adicional: e.target.value })} /></div>
              <div className="flex items-center justify-between rounded-lg bg-slate-950/60 px-3 py-3"><Label>Módulo ativo</Label><Switch disabled={!owner} checked={edit.ativo !== false} onCheckedChange={(v) => setEdit({ ...edit, ativo: v })} /></div>
            </div>}
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><SaveButton type="submit" disabled={busy} className="bg-violet-700 hover:bg-violet-600">Salvar</SaveButton></DialogFooter>
          </SaveForm>
        </SaveDialogContent>
      </Dialog>
    </div>
  );
}
